import React, { useState } from 'react';
import { Card, CardContent } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../../components/ui/dropdown-menu';
import { MoreHorizontal, Clock, Copy, Mail, Trash2 } from 'lucide-react';
import { tempGuestAPI } from '../../../services/api';
import { toast } from 'sonner';

export const PendingPreScreenTable = ({ tokens = [], onRefresh, onSendNewLink }) => {
  const [loading, setLoading] = useState(false);

  if (!tokens || tokens.length === 0) {
    return (
      <Card>
        <CardContent className="p-4">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-semibold flex items-center gap-2">
              <Clock className="h-4 w-4 text-amber-600" />
              Pending Pre-Screen Invitations (0)
            </h3>
            {onSendNewLink && (
              <Button onClick={onSendNewLink} className="gap-2">
                <Mail className="w-4 h-4" />
                Send New Link
              </Button>
            )}
          </div>
          <div className="py-8 text-center">
            <Clock className="h-10 w-10 mx-auto mb-2 text-muted-foreground opacity-50" />
            <p className="text-muted-foreground">No pending pre-screen invitations.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString();
    } catch {
      return '-';
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleString();
    } catch {
      return '-';
    }
  };

  const getExpiryStatus = (expiresAt) => {
    if (!expiresAt) return { label: 'No expiry', color: 'outline' };

    const now = new Date();
    const expiry = new Date(expiresAt);
    const daysLeft = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));

    if (daysLeft <= 0) {
      return { label: 'Expired', color: 'destructive' };
    } else if (daysLeft <= 3) {
      return { label: `${daysLeft}d left`, color: 'warning' };
    } else {
      return { label: `${daysLeft}d left`, color: 'secondary' };
    }
  };

  // UUID format (with dashes) = guest-based record; base64url (no dashes) = legacy link token
  const isGuestRecord = (record) => /^[0-9a-f-]{36}$/.test(record.token);

  const handleCopyLink = (token) => {
    const url = `${window.location.origin}/prescreen?token=${token}`;
    navigator.clipboard.writeText(url);
    toast.success('Link copied to clipboard!');
  };

  const handleResendEmail = async (record) => {
    if (!record.staff_email) {
      toast.error('No email address on file for this invitation');
      return;
    }

    setLoading(true);
    try {
      await tempGuestAPI.resendInvite(record.id);
      toast.success(`Invitation resent to ${record.staff_email}!`);
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to resend invitation');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-semibold flex items-center gap-2">
            <Clock className="h-4 w-4 text-amber-600" />
            Pending Pre-Screen Invitations ({tokens.length})
          </h3>
          {onSendNewLink && (
            <Button onClick={onSendNewLink} className="gap-2">
              <Mail className="w-4 h-4" />
              Send New Link
            </Button>
          )}
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Staff Member</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Entity</TableHead>
              <TableHead>Created</TableHead>
              <TableHead>Expires</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-12"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {tokens.map((token) => {
              const expiryStatus = getExpiryStatus(token.expires_at);
              return (
                <TableRow key={token.id} className="hover:bg-muted/50">
                  <TableCell className="font-medium">
                    {token.staff_name || '(No name provided)'}
                  </TableCell>

                  <TableCell className="text-sm">
                    {token.staff_email ? (
                      <a href={`mailto:${token.staff_email}`} className="text-blue-600 hover:underline">
                        {token.staff_email}
                      </a>
                    ) : (
                      <span className="text-muted-foreground">(No email)</span>
                    )}
                  </TableCell>

                  <TableCell className="text-sm">{token.entity_id || '-'}</TableCell>

                  <TableCell className="text-sm">{formatDate(token.created_at)}</TableCell>

                  <TableCell className="text-sm">
                    {token.expires_at ? formatDate(token.expires_at) : 'Never'}
                  </TableCell>

                  <TableCell>
                    <Badge variant={expiryStatus.color}>
                      {expiryStatus.label}
                    </Badge>
                  </TableCell>

                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" disabled={loading}>
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {!isGuestRecord(token) && (
                          <DropdownMenuItem onClick={() => handleCopyLink(token.token)}>
                            <Copy className="h-4 w-4 mr-2" />
                            Copy Link
                          </DropdownMenuItem>
                        )}
                        {token.staff_email && (
                          <DropdownMenuItem onClick={() => handleResendEmail(token)}>
                            <Mail className="h-4 w-4 mr-2" />
                            Resend Email
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};
